/**
 * core/renderer.ts — Renderer configuration, resolution governor, dev HUD stats
 *
 * Governor ladder (only while presenting):
 *   Step 1: session.updateTargetFrameRate(72)
 *   Step 2: reduce content (planet LOD, starfield cap, hide Milky Way)
 *   Step 3: write localStorage['sv.fbScale'] for next-session boot
 *
 * Each tier has: 3s warm-up blackout after session start,
 * 3s cooldown between escalations, and a per-tier latch.
 */

import * as THREE from 'three';

// ── Renderer Config ────────────────────────────────────────────
let renderer: THREE.WebGLRenderer;
let _currentFbScale = 1.0;

/** Call at boot before any session request */
export function applyBootScale(): void {
  const stored = localStorage.getItem('sv.fbScale');
  if (stored) {
    const v = parseFloat(stored);
    if (v >= 0.85 && v <= 1.0) {
      _currentFbScale = v;
    }
  }
}

export function createRenderer(): THREE.WebGLRenderer {
  applyBootScale();

  renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: 'high-performance',
  });
  renderer.xr.enabled = true;
  renderer.xr.setFramebufferScaleFactor(_currentFbScale);
  renderer.xr.setFoveation(1.0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.75;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.setSize(window.innerWidth, window.innerHeight);

  return renderer;
}

// ── Governor ───────────────────────────────────────────────────
export type GovernorTier = 0 | 1 | 2 | 3;

// Warm-up / cooldown constants
const WARMUP_MS = 3000;       // ignore first 3s of session
const COOLDOWN_MS = 3000;     // wait 3s after each escalation before next arm
const SLOW_THRESHOLD = 30;    // consecutive slow frames to trigger
const FAST_THRESHOLD = 120;   // consecutive fast frames to recover

let _tier: GovernorTier = 0;
let _sessionStartTime = 0;
let _lastEscalationTime = 0;
let _slowFrames = 0;
let _fastFrames = 0;
let _fbScalePending = false;

// Callbacks set by main.ts for content degradation
let _onDegradeContent: (() => void) | null = null;
let _onRestoreContent: (() => void) | null = null;

export function setContentCallbacks(degrade: () => void, restore: () => void): void {
  _onDegradeContent = degrade;
  _onRestoreContent = restore;
}

export function getGovernorTier(): GovernorTier {
  return _tier;
}

export function isFbScalePending(): boolean {
  return _fbScalePending;
}

/** Call on sessionstart to reset governor state */
export function onSessionStart(): void {
  _sessionStartTime = performance.now();
  _lastEscalationTime = _sessionStartTime;
  _slowFrames = 0;
  _fastFrames = 0;
  // Don't reset _tier — carry over degradation if persisted
}

export function updateResolutionGovernor(frameTimeMs: number): void {
  if (!renderer.xr.isPresenting) return;

  const session = renderer.xr.getSession();
  const hz = session?.frameRate ?? 90;
  const budgetMs = (1000 / hz) * 0.95;
  const recoveryMs = (1000 / hz) * 0.80;

  const now = performance.now();

  // Warm-up blackout: ignore first 3s of session (shader compiles, pipeline warm)
  if (now - _sessionStartTime < WARMUP_MS) return;

  // Cooldown: wait 3s after last escalation before re-arming
  if (now - _lastEscalationTime < COOLDOWN_MS) return;

  // Per-tier latch: once we hit tier 3, don't further escalate
  if (_tier >= 3) {
    // Recovery path only
    if (frameTimeMs < recoveryMs) {
      _fastFrames++;
      _slowFrames = 0;
      if (_fastFrames >= FAST_THRESHOLD) {
        _tier = (_tier - 1) as GovernorTier;
        _applyTier(_tier);
        _fastFrames = 0;
        _lastEscalationTime = now;
      }
    } else {
      _fastFrames = 0;
    }
    return;
  }

  if (frameTimeMs > budgetMs) {
    _slowFrames++;
    _fastFrames = 0;

    if (_slowFrames >= SLOW_THRESHOLD) {
      _tier = (_tier + 1) as GovernorTier;
      _applyTier(_tier);
      _slowFrames = 0;
      _lastEscalationTime = now;
      console.log(`[GOV] Escalated to tier ${_tier} (${frameTimeMs.toFixed(1)}ms > ${budgetMs.toFixed(1)}ms @${hz}Hz)`);
    }
  } else if (frameTimeMs < recoveryMs) {
    _fastFrames++;
    _slowFrames = 0;

    if (_fastFrames >= FAST_THRESHOLD && _tier > 0) {
      _tier = (_tier - 1) as GovernorTier;
      _applyTier(_tier);
      _fastFrames = 0;
      _lastEscalationTime = now;
      console.log(`[GOV] Recovered to tier ${_tier}`);
    }
  } else {
    // Normal frame — reset slow counter but keep tier
    _slowFrames = 0;
    _fastFrames = 0;
  }
}

function _applyTier(tier: GovernorTier): void {
  switch (tier) {
    case 0:
      renderer.xr.getSession()?.updateTargetFrameRate?.(90).catch(() => {});
      if (_onRestoreContent) _onRestoreContent();
      _fbScalePending = false;
      break;
    case 1:
      renderer.xr.getSession()?.updateTargetFrameRate?.(72).catch(() => {});
      break;
    case 2:
      if (_onDegradeContent) _onDegradeContent();
      break;
    case 3:
      localStorage.setItem('sv.fbScale', '0.92');
      _fbScalePending = true;
      break;
  }
}

export function getFramebufferScale(): number {
  return _currentFbScale;
}

// ── Renderer Stats (for HUD) ───────────────────────────────────
export function getRendererStats() {
  const info = renderer.info;
  return {
    drawCalls: info.render.calls,
    triangles: info.render.triangles,
    fbScale: _currentFbScale,
    inXR: renderer.xr.isPresenting,
  };
}

export { renderer };
